import {useEffect, useRef, useState} from "react";

type UsePagePresenceOptions = {
    onExit?: () => void;   // fired when user leaves focus/visibility (blur or hidden)
    onEnter?: () => void;  // fired when user returns (focus or visible)
};

type PresenceState = {
    isVisible: boolean;
    hasFocus: boolean;
    isPresent: boolean; // visible AND focused
};

const safeDoc = typeof document !== "undefined" ? document : ({} as Document);
const getPresence = (): PresenceState => {
    const isVisible = safeDoc.hidden === undefined ? true : !safeDoc.hidden;
    const hasFocus = typeof safeDoc.hasFocus === "function" ? safeDoc.hasFocus() : true;
    return { isVisible, hasFocus, isPresent: isVisible && hasFocus };
};

export const usePagePresence = ({ onExit, onEnter }: UsePagePresenceOptions = {}) => {
    const [state, setState] = useState<PresenceState>(getPresence);
    const prevPresentRef = useRef<boolean>(state.isPresent);
    const stateRef = useRef<PresenceState>(state);

    useEffect(() => {
        stateRef.current = state;
    }, [state]);

    useEffect(() => {
        let frame = 0;

        const recompute = () => {
            // Coalesce multiple rapid events into a single calc
            if (frame) return;
            frame = requestAnimationFrame(() => {
                frame = 0;
                const next = getPresence();
                const prev = stateRef.current;

                // Avoid unnecessary state updates
                if (
                    prev.isVisible !== next.isVisible ||
                    prev.hasFocus !== next.hasFocus ||
                    prev.isPresent !== next.isPresent
                ) {
                    setState(next);
                }

                // Fire callbacks only on presence edge changes
                const prevPresent = prevPresentRef.current;
                if (prevPresent !== next.isPresent) {
                    prevPresentRef.current = next.isPresent;
                    if (next.isPresent) {
                        onEnter && onEnter();
                    } else {
                        onExit && onExit();
                    }
                }
            });
        };

        const onVisibilityChange = () => recompute();
        const onFocus = () => recompute();
        const onBlur = () => recompute();

        const onPageHide = () => recompute();
        const onPageShow = () => recompute();

        document.addEventListener("visibilitychange", onVisibilityChange, { passive: true });
        window.addEventListener("focus", onFocus, { passive: true });
        window.addEventListener("blur", onBlur, { passive: true });
        window.addEventListener("pagehide", onPageHide, { passive: true });
        window.addEventListener("pageshow", onPageShow, { passive: true });

        // Initial sync
        recompute();

        return () => {
            if (frame) cancelAnimationFrame(frame);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("pageshow", onPageShow);
        };
        // Important: depend only on callbacks so the effect doesn't re-attach on every state change
    }, [onEnter, onExit]);

    return state; // { isVisible, hasFocus, isPresent }
};